import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
    BarChart3, Users, FileText, PenTool, Star, Trophy,
    TrendingUp, Download, RefreshCw, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { LeagueBadge } from '../../components/gamification';

// Animation variants
const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
};

const stagger = {
    visible: { transition: { staggerChildren: 0.1 } }
};

function StatCard({ title, value, subtitle, icon: Icon, gradient }) {
    return (
        <motion.div variants={fadeIn} whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}>
            <div className="relative group bg-card backdrop-blur-sm rounded-2xl p-6 border border-border hover:border-white/20 transition-all duration-300 overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                <div className="relative flex items-start justify-between">
                    <div>
                        <p className="text-sm text-muted-foreground">{title}</p>
                        <p className="text-3xl font-black text-foreground mt-1">{value}</p>
                        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
                    </div>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg`}>
                        <Icon className="w-6 h-6 text-white" />
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

export default function AgencyStatsPage() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [dateRange, setDateRange] = useState({ start: '', end: '' });

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        setLoading(true);
        try {
            const response = await api.get('/statistics/agency');
            setStats(response.data);
        } catch (error) {
            console.error('Error loading stats:', error);
            toast.error('Erreur de chargement des statistiques');
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = {};
            if (dateRange.start) params.start_date = dateRange.start;
            if (dateRange.end) params.end_date = dateRange.end;
            
            const response = await api.get('/export/agency/mandates', { params });
            
            const data = response.data.data;
            if (!data || data.length === 0) {
                toast.error('Aucune donnée à exporter');
                return;
            }
            
            const headers = Object.keys(data[0]);
            const csvContent = [
                headers.join(','),
                ...data.map(row => headers.map(h => {
                    const val = row[h];
                    if (typeof val === 'object') return JSON.stringify(val).replace(/,/g, ';');
                    return `"${String(val || '').replace(/"/g, '""')}"`;
                }).join(','))
            ].join('\n');
            
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `mandats_${new Date().toISOString().split('T')[0]}.csv`;
            link.click();
            
            toast.success(`Export téléchargé (${data.length} mandats)`);
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Erreur lors de l\'export');
        } finally {
            setExporting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-[#681793] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <motion.div 
            className="space-y-8"
            initial="hidden"
            animate="visible"
            variants={stagger}
        >
            {/* Header */}
            <motion.div variants={fadeIn} className="flex items-center justify-between">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <BarChart3 className="w-7 h-7 text-[#681793]" />
                        <h1 className="text-3xl font-black text-foreground">Statistiques</h1>
                    </div>
                    <p className="text-muted-foreground">Performance de votre agence</p>
                </div>
                <Button 
                    onClick={loadStats} 
                    variant="outline"
                    className="border-border text-muted-foreground hover:text-foreground hover:bg-card"
                >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualiser
                </Button>
            </motion.div>

            {/* KPI Cards */}
            <motion.div variants={stagger} className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard 
                    title="Mandats créés" 
                    value={stats?.usage?.mandates_created || 0}
                    icon={FileText}
                    gradient="from-[#681793] to-[#9b4dca]"
                />
                <StatCard 
                    title="Signatures" 
                    value={stats?.usage?.signatures_completed || 0}
                    subtitle={`${stats?.usage?.signature_completion_rate || 0}% complétées`}
                    icon={PenTool}
                    gradient="from-green-500 to-emerald-400"
                />
                <StatCard 
                    title="Avis reçus" 
                    value={stats?.quality?.ratings_confirmed || 0}
                    icon={Star}
                    gradient="from-[#FF914D] to-[#ffb380]"
                />
                <StatCard 
                    title="HomlyScore moyen équipe" 
                    value={Math.round(stats?.team?.avg_team_elo || 1000)}
                    icon={Trophy}
                    gradient="from-blue-500 to-cyan-400"
                />
            </motion.div>

            {/* Team Performance */}
            <motion.div variants={fadeIn}>
                <div className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden">
                    <div className="p-6 border-b border-border">
                        <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                            <Users className="w-5 h-5 text-[#FF914D]" />
                            Performance de l'équipe
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            {stats?.team?.agents?.length || 0} agents • {stats?.team?.pending_invites || 0} invitations en attente
                        </p>
                    </div>
                    <div className="p-6">
                        {stats?.team?.agents?.length > 0 ? (
                            <div className="space-y-3">
                                {stats.team.agents.map((agent, idx) => (
                                    <motion.div 
                                        key={agent.id} 
                                        className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border hover:border-border transition-all"
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: idx * 0.05 }}
                                    >
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center text-white font-bold">
                                            {agent.name?.charAt(0) || '?'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-foreground truncate">{agent.name}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {agent.mandates_count} mandats • {agent.signatures_count} signatures
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <LeagueBadge league={agent.league} size="sm" />
                                            <div className="text-right">
                                                <p className="font-bold text-foreground">{Math.round(agent.elo)}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    {agent.avg_rating?.toFixed(1) || '—'}/5 ({agent.total_ratings} avis)
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-3">
                                    <Users className="w-7 h-7 text-muted-foreground" />
                                </div>
                                <p className="text-muted-foreground">Aucun agent dans l'équipe</p>
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>

            {/* Export Section */}
            <motion.div variants={fadeIn}>
                <div className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden">
                    <div className="p-6 border-b border-border">
                        <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                            <Download className="w-5 h-5 text-[#681793]" />
                            Export des mandats
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">Téléchargez vos données au format CSV</p>
                    </div>
                    <div className="p-6">
                        <div className="flex flex-wrap gap-4 items-end">
                            <div className="space-y-2">
                                <Label className="text-muted-foreground">Date début</Label>
                                <Input 
                                    type="date" 
                                    value={dateRange.start}
                                    onChange={(e) => setDateRange(d => ({ ...d, start: e.target.value }))}
                                    className="bg-card border-border text-foreground"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-muted-foreground">Date fin</Label>
                                <Input 
                                    type="date" 
                                    value={dateRange.end}
                                    onChange={(e) => setDateRange(d => ({ ...d, end: e.target.value }))}
                                    className="bg-card border-border text-foreground"
                                />
                            </div>
                            <Button 
                                onClick={handleExport}
                                disabled={exporting}
                                className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white"
                            >
                                {exporting ? (
                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Download className="w-4 h-4 mr-2" />
                                )}
                                Exporter les mandats
                            </Button>
                        </div>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
}
